import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from './firebaseConfig';
import { Lock, CreditCard, LogOut, Server, AlertTriangle, ShieldX } from 'lucide-react';

// Import your screens correctly
import Login from './components/Login';
import Dashboard from './components/Dashboard'; 
import ClientDashboard from './components/ClientDashboard';
import RateReview from './components/RateReview';
import LandingPage from './components/LandingPage'; // 🔥 NEW: Hamara Landing Page Import

// 🚦 Payment Required Screen
const PaymentRequiredScreen = ({ email }) => (
  <div className="h-screen w-full flex flex-col items-center justify-center bg-[#F8FAFC] p-6 text-slate-900 font-sans selection:bg-indigo-100">
    <div className="bg-white p-10 rounded-2xl shadow-xl border border-slate-200 flex flex-col items-center text-center max-w-md w-full relative">
      <div className="bg-amber-50 p-4 rounded-xl mb-6 border border-amber-100 shadow-sm flex items-center justify-center">
        <Lock size={32} className="text-amber-600" />
      </div>
      <h2 className="text-2xl font-black text-slate-900 mb-3 tracking-tight">Subscription Inactive</h2>
      <p className="text-sm text-slate-600 font-medium mb-8 leading-relaxed px-4">
        Your billing cycle has ended or the account is suspended. Please upgrade your plan to restore AI Automation services.
      </p>
      <div className="w-full space-y-3">
        <button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-black py-3.5 rounded-xl transition-all shadow-md flex items-center justify-center gap-2 uppercase text-[10px] tracking-widest">
          <CreditCard size={16}/> Upgrade Plan
        </button>
        <button onClick={() => signOut(auth)} className="w-full bg-slate-50 hover:bg-slate-100 text-slate-600 font-bold py-3.5 rounded-xl transition-all flex items-center justify-center gap-2 text-[10px] uppercase tracking-widest border border-slate-200">
          <LogOut size={16}/> Return to Login
        </button>
      </div>
    </div>
  </div>
);

// 🛡️ THE SECURE APP (Aapka Purana App.js Logic Bina Kisi Change Ke)
const ProtectedApp = () => {
  const [user, setUser] = useState(null);
  const [roleData, setRoleData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setLoading(true);
      if (currentUser) {
        console.log("UID:", currentUser.uid);
        console.log("EMAIL:", currentUser.email);
        setUser(currentUser);

        // 👑 SuperAdmin Bypass
        const SUPER_ADMINS = ["saurabhdhiman1100@gmail.com", "pivoteduunit@gmail.com"];

        if (SUPER_ADMINS.includes(currentUser.email)) {
           setRoleData({ role: 'admin' });
           setLoading(false);
           return;
        }

        try {
          const userDocRef = doc(db, "dashboard_users", currentUser.uid);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const userData = userDoc.data();
            if (userData.role === 'admin' || userData.role === 'superadmin') {
                setRoleData({ role: 'admin' });
            } else if (userData.role === 'client') {
                const clientDoc = await getDoc(doc(db, "clients", userData.clientId));
                if (clientDoc.exists()) {
                    setRoleData({ role: 'client', ...clientDoc.data() });
                } else {
                    setRoleData({ role: 'client', status: 'UNKNOWN' });
                }
            } else {
                setRoleData({ role: 'unauthorized' });
            }
          } else {
            setRoleData({ role: 'unauthorized' });
          }
        } catch (error) {
          console.error("Auth Fetch Error:", error);
          setRoleData({ role: 'error' });
        }
      } else {
        setUser(null);
        setRoleData(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading) return (
    <div className="h-screen w-full flex flex-col items-center justify-center bg-[#F8FAFC] gap-5">
      <div className="relative">
          <div className="h-14 w-14 rounded-xl border-4 border-indigo-100 border-t-indigo-600 animate-spin"></div>
          <div className="absolute inset-0 flex items-center justify-center"><Server size={20} className="text-indigo-600 animate-pulse"/></div>
      </div>
      <p className="font-black tracking-widest text-[10px] uppercase text-slate-400">Initializing Engine...</p>
    </div>
  );

  if (!user) return <Login />;

  if (roleData?.role === 'admin') {
    return <Dashboard />;
  }

  if (roleData?.role === 'client') {
    const expiryDate = roleData.renewalDate?.toDate ? roleData.renewalDate.toDate() : null;
    const isExpired = expiryDate && new Date() > expiryDate;
    
    if ((isExpired && roleData.status !== 'ACTIVE') || roleData.status === 'SUSPENDED') {
        return <PaymentRequiredScreen email={user.email} />;
    }
    
    return <ClientDashboard clientId={roleData.clientId} />;
  }

  return (
    <div className="h-screen w-full flex items-center justify-center bg-[#F8FAFC] p-6 selection:bg-rose-100">
      <div className="bg-white border border-rose-100 p-10 rounded-2xl shadow-xl max-w-md w-full relative">
        <div className="w-14 h-14 bg-rose-50 border border-rose-100 rounded-xl flex items-center justify-center mb-6">
            <ShieldX size={24} className="text-rose-600"/>
        </div>
        <h2 className="text-xl font-black text-slate-900 mb-2 tracking-tight uppercase">Access Denied</h2>
        <p className="text-slate-600 font-medium mb-6 text-sm leading-relaxed">
            The account <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{user.email}</span> does not have a registered role in the system.
        </p>
        <button onClick={() => signOut(auth)} className="w-full bg-slate-900 text-white hover:bg-slate-800 px-6 py-3.5 rounded-xl font-black uppercase text-[10px] tracking-widest transition-all shadow-md flex items-center justify-center gap-2">
            <LogOut size={16}/> Terminate Session
        </button>
      </div>
    </div>
  );
};

// 🌐 THE MAIN APP WRAPPER (Handling Public vs Private Routes)
function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* 🌟 PUBLIC SAAS LANDING PAGE (Meta Approval Ke Liye) */}
        <Route path="/" element={<LandingPage />} />

        {/* 🌟 PUBLIC ROUTE: Koi bhi QR scan karke yahan aa sakta hai */}
        <Route path="/rate/:clientId" element={<RateReview />} />
        
        {/* 🔒 PROTECTED ROUTE: /login ya baaki koi bhi link yahan se guzrega */}
        <Route path="/*" element={<ProtectedApp />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;